import authService from './AuthService';
import { ApiViewResponseError, ApiViewResponse } from '../typings';
import { showGlobalError } from '../components/GlobalErrorProvider';
import { logger } from './logger';

const friendlyMessages: Record<number, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  408: 'The request timed out. Please try again.',
  409: 'A conflict occurred. The resource may have been modified by another user.',
  500: 'An internal server error occurred. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The service is temporarily unavailable. Please try again later.',
};

// Renew slightly before expiry so a token doesn't die mid-flight; also absorbs
// modest clock skew (expiry is checked against the local clock).
const EXPIRY_BUFFER_SECONDS = 30;

class ApiService {
  public fetch(url: RequestInfo, opts: RequestInit, retry?: boolean): Promise<Response>;
  public fetch(url: RequestInfo, formData: FormData, retry?: boolean): Promise<Response>;
  public async fetch(url: RequestInfo, optsOrFormData: RequestInit | FormData, retry: boolean = false): Promise<Response> {
    const user = await this.getFreshUser();
    const isFormData = optsOrFormData instanceof FormData;
    const config: RequestInit = isFormData
      ? { method: 'POST', body: optsOrFormData, headers: { 'Authorization': `bearer ${user?.access_token}` } }
      : { ...optsOrFormData, headers: { 'Content-Type': 'application/json', 'Authorization': `bearer ${user?.access_token}` } };

    let res: Response;
    try {
      res = await fetch(url, config);
    } catch (err: any) {
      const message = err instanceof TypeError && err.message === 'Failed to fetch'
        ? 'Unable to connect to the server. Please check your network connection and try again.'
        : `An unexpected error occurred: ${err.message ?? err}`;
      showGlobalError(message, err?.stack ?? String(err));
      throw err;
    }

    // Fallback for anything the local expiry check can't see (token revoked
    // server-side, key rollover, clock skew): renew once and retry.
    if (res.status === 401 && !retry) {
      await this.renewOrLogin();
      return isFormData
        ? this.fetch(url, optsOrFormData as FormData, true)
        : this.fetch(url, optsOrFormData as RequestInit, true);
    }

    if (res.ok) {
      return res;
    }
    throw res;
  }

  // Proactive renewal: token expiry is checked locally (oidc-client-ts derives
  // expired/expires_in from the stored expires_at) — no network round-trip.
  private async getFreshUser() {
    let user = await authService.getUser();
    if (!user || user.expired || (user.expires_in !== undefined && user.expires_in < EXPIRY_BUFFER_SECONDS)) {
      await this.renewOrLogin();
      user = await authService.getUser();
    }
    return user;
  }

  private async renewOrLogin(): Promise<void> {
    try {
      await authService.renewToken();
    } catch (err) {
      logger.error('Token renewal failed, redirecting to sign-in.', err);
      await authService.removeUser();
      await authService.login({ state: { pathname: window.location.pathname } });
      throw new Error('Your session has expired. Redirecting to sign-in.');
    }
  }
}

export const getBodyErrors = async (err: any): Promise<ApiViewResponseError[]> => {
  try {
    if (err.status === 403) {
      return [{ detail: 'You do not have permission to perform this action.', title: 'Forbidden', code: '403', id: '' }];
    }
    if (err.status && friendlyMessages[err.status]) {
      try {
        const apiResponseBody = await err.json() as ApiViewResponse;
        if (apiResponseBody.errors?.length > 0) {
          return apiResponseBody.errors;
        }
      } catch { /* body not parseable, use friendly message */ }
      return [{ detail: friendlyMessages[err.status], title: 'Error', code: String(err.status), id: '' }];
    }
    if (err.json) {
      try {
        const apiResponseBody = await err.json() as ApiViewResponse;
        if (apiResponseBody.errors?.length > 0) {
          return apiResponseBody.errors;
        }
      } catch { /* body not parseable */ }
    }
    const detail = err.message ?? err.toString();
    showGlobalError(detail, err?.stack);
    return [{ detail, title: 'Error', code: '', id: '' }];
  } catch (parseErr: any) {
    const message = 'An unexpected error occurred while processing the server response.';
    showGlobalError(message, `Original error: ${err}\nParse error: ${parseErr}`);
    return [{ detail: message, title: 'Error', code: '', id: '' }];
  }
}

const apiService = new ApiService();
export default apiService;
